import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { kycApi, adminKYCApi } from '@/api';
import { Container, Flex } from '@/ui/layout';
import { Body2Light, Body3Light } from '@/ui/typography';
import { useTranslation } from 'react-i18next';
import { Checkbox, Modal } from 'antd';
import { Button, Select } from '@/components';
import { usePopups } from '@/hooks';

export const AddProductModal = ({ visible = false, onClose, user, onSuccess }) => {
  switch (user) {
    case 'investor': {
      return <AddModalInvestor visible={visible} onClose={onClose} onSuccess={onSuccess} />;
    }
    case 'admin': {
      return <AddModalAdmin visible={visible} onClose={onClose} onSuccess={onSuccess} />;
    }
    default:
      return null;
  }
};

const AddModalInvestor = ({ onSuccess, visible, ...props }) => {
  const { showErrorToast } = usePopups();
  const { data, refetch } = useQuery({
    queryKey: ['getAllowedProductsToAdd'],
    queryFn: kycApi.getAllowedProductsToAdd
  });
  const { mutateAsync } = useMutation({ mutationFn: kycApi.requestProduct });

  useEffect(() => {
    if (visible) refetch();
  }, [visible]);

  const allowedProductsList = useMemo(() => (data ? data.data : null), [data]);

  const onAdd = async (productId) => {
    try {
      await mutateAsync(productId);
      if (onSuccess) await onSuccess();
    } catch (e) {
      console.error(e);
      showErrorToast(e, true);
    }
  };

  return (
    <AddModal
      allowedProductsList={allowedProductsList}
      onAdd={onAdd}
      visible={visible}
      {...props}
    />
  );
};

const AddModalAdmin = ({ onSuccess, visible, ...props }) => {
  const { id } = useParams();
  const { showErrorToast } = usePopups();
  const { data, refetch } = useQuery({
    queryKey: ['getAllowedProductsToAdd'],
    queryFn: () => adminKYCApi.getAllowedProductsToAdd({ investorId: id })
  });
  const { mutateAsync } = useMutation({ mutationFn: adminKYCApi.requestProduct });

  useEffect(() => {
    if (visible) refetch();
  }, [visible]);

  const allowedProductsList = useMemo(() => (data ? data.data : null), [data]);

  const onAdd = async (productId, onlyAdmin) => {
    try {
      await mutateAsync({ investorId: id, productId: productId, onlyAdmin: onlyAdmin });
      if (onSuccess) await onSuccess();
    } catch (e) {
      console.error(e);
      showErrorToast(e, true);
    }
  };

  return (
    <AddModal
      allowedProductsList={allowedProductsList}
      onAdd={onAdd}
      visible={visible}
      {...props}
      adminView={true}
    />
  );
};

const AddModal = ({ allowedProductsList, onAdd, visible, onClose, adminView }) => {
  const { t } = useTranslation();
  const { showErrorToast, showInfoToast } = usePopups();
  const [selected, setSelected] = useState(null);
  const [onlyAdmin, setOnlyAdmin] = useState(false);
  const [disabled, setDisabled] = useState(false);

  const onAddProduct = async () => {
    setDisabled(true);
    try {
      const selectedProduct = allowedProductsList.find((i) => i.id === selected);
      if (selectedProduct) {
        await onAdd(selectedProduct.id, onlyAdmin);
        showInfoToast('Successfully added');
        setSelected(null);
        setDisabled(false);
        onClose();
      }
    } catch (e) {
      console.error(e);
      showErrorToast(e, true);
      setDisabled(false);
    }
  };

  return (
    <Modal open={visible} onCancel={onClose} closable footer={null} centered>
      <Container $paddingTop={30}>
        {allowedProductsList && allowedProductsList.length > 0 && (
          <Select
            allowClear
            placeholder={t('Products allowed to add')}
            options={allowedProductsList.map((item) => {
              return { label: String(item.name).toLocaleLowerCase(), value: item.id };
            })}
            value={selected}
            onChange={(value) => setSelected(value)}
          />
        )}
        {adminView && (
          <Body3Light $marginTop={15} $inline>
            <Checkbox
              name='OnlyAdmin'
              value={onlyAdmin}
              checked={onlyAdmin}
              onChange={(e) => setOnlyAdmin(e.target.checked)}
              style={{ fontSize: '15px' }}
            >
              {t('Only Admin')}
            </Checkbox>
          </Body3Light>
        )}
        {allowedProductsList && allowedProductsList.length === 0 && (
          <Body2Light>{t('Not allowed products to add')}</Body2Light>
        )}
        <Flex $marginTop={8} justify='flex-end'>
          <Container $width={120}>
            <Button text={t('Add')} onClick={onAddProduct} disabled={!selected || disabled} />
          </Container>
        </Flex>
      </Container>
    </Modal>
  );
};
